import { Fragment, JSX, useState } from 'react';
import { ExtendedNestedSubAccount } from '../OrgChartContainer';
import { Box, Card, CircleButton, formattedCurrency, NoFillButton, withStyles } from 'vopay';
import styles from './OrgDetails.module.scss';

import CardRows from '@/components/UI/CardRows';
import TransactionStatus from '@/components/UI/TransactionStatus/TransactionStatus';
import { Button } from '@/components/UI';
import { useTranslation } from 'react-i18next';
const classes = withStyles(styles);

interface OrgDetailsProps {
  node: ExtendedNestedSubAccount;
  clientAccountsEnabled: boolean;
  canCreateAccountTransfer: boolean;
  accountBlocked: boolean;
  canManageBlocking: boolean;
  onClose: () => void;
  onCreateSubAccount: () => void;
  onAccountTransfer: () => void;
  onLocationChange: (location: string, state?: unknown) => void;
  onBlockAccount: (accountID: string, accountName: string) => void;
  onUnblockAccount: (accountID: string, accountName: string) => void;
}

const OrgDetails: React.FC<OrgDetailsProps> = ({
  node,
  clientAccountsEnabled,
  canCreateAccountTransfer,
  accountBlocked,
  canManageBlocking,
  onClose,
  onCreateSubAccount,
  onAccountTransfer,
  onLocationChange,
  onBlockAccount,
  onUnblockAccount,
}) => {
  const { t } = useTranslation('translations', { keyPrefix: 'SubAccounts.List' });
  const isBlocked = node.AccountStatus === 'Blocked';

  const handleBlockClick = () => {
    onClose();
    if (isBlocked) {
      onUnblockAccount(node.AccountID, node.AccountName);
    } else {
      onBlockAccount(node.AccountID, node.AccountName);
    }
  };

  return (
    <div className={classes('card')}>
      <div data-testid={`orgDetails-${node.AccountID.replace(/\s+/g, '')}`} className={classes('orgDetailsContainer')}>
        <Box flx="col" gapSize="15px">
          <Box jc="fe">
            <CircleButton onClick={onClose} icon="close" />
          </Box>
          <Card style={{ padding: '24px', backdropFilter: '0px 10px 24px rgb(0, 0, 0)' }}>
            <Box jc="sb" ai="fs">
              <Box flx="col">
                <div className={classes('BreadCrumb')}>{getBreadcrumbs(node.breadcrumb, () => {})}</div>
                <button
                  className={classes('linkButton')}
                  onClick={() => {
                    onLocationChange(
                      `/sub-accounts-list/details/${node.AccountID.replace(/\s+/g, '')}/${node.Currency}`,
                      { node },
                    );
                  }}
                >
                  {node.AccountName}
                </button>
              </Box>
              {canManageBlocking && (
                <NoFillButton
                  colorScheme={isBlocked ? 'neutral' : 'destructive'}
                  size="small"
                  iconLeft={isBlocked ? 'lock_open' : 'block'}
                  onClick={handleBlockClick}
                >
                  {isBlocked
                    ? t('OrgChart.OrgDetails.unblockButton', { defaultValue: 'Unblock Sub-Account' })
                    : t('OrgChart.OrgDetails.blockButton', { defaultValue: 'Block Sub-Account' })}
                </NoFillButton>
              )}
            </Box>
          </Card>
          <Card data-testid={`orgDetails-Card-${node.AccountID.replace(/\s+/g, '')}`} style={{ padding: '10px 12px' }}>
            <Box flx="col" className="orgItemTitle">
              <CardRows
                rows={[
                  {
                    key: '1',
                    left: t('Attributes.status', { defaultValue: 'Status' }),
                    right: (
                      <Box jc="fe">
                        <TransactionStatus
                          status={isBlocked ? 'blocked' : node.IsActive ? 'active' : 'not active'}
                          label={
                            isBlocked
                              ? t('Attributes.blockedBadge', { defaultValue: 'Blocked' })
                              : node.IsActive
                                ? t('Attributes.activeBadge', { defaultValue: 'Active' })
                                : t('Attributes.notActiveBadge', { defaultValue: 'Not Active' })
                          }
                        />
                      </Box>
                    ),
                  },
                  {
                    key: '2',
                    left: t('Attributes.pendingBalance', { defaultValue: 'Pending Balance' }),
                    right: isBlocked ? 'N/A' : `${node.Currency} $${formattedCurrency(node.PendingBalance)}`,
                  },
                  {
                    key: '3',
                    left: t('Attributes.availableBalance', { defaultValue: 'Available Balance' }),
                    right: isBlocked ? 'N/A' : `${node.Currency} $${formattedCurrency(node.AvailableBalance)}`,
                  },
                  ...(node.RollupAvailableBalance && !isBlocked
                    ? [
                        {
                          key: '4',
                          left: t('Attributes.rollUpBalance', {
                            defaultValue: 'Available Roll Up Balance',
                          }),
                          right: `${node.Currency} $${formattedCurrency(node.RollupAvailableBalance)}`,
                        },
                      ]
                    : []),
                ]}
              />
            </Box>
          </Card>
          {node.IsActive && (
            <div className={classes('buttonContainer')}>
              <Button
                onClick={onCreateSubAccount}
                colorScheme="secondary"
                iconLeft="add"
                className={classes('buttonFullWidth')}
              >
                {t('OrgChart.OrgDetails.createSubAccountButton', { defaultValue: 'Sub-Account' })}
              </Button>
              {!clientAccountsEnabled && !accountBlocked && !isBlocked && canCreateAccountTransfer && (
                <Button onClick={onAccountTransfer} colorScheme="primary" className={classes('buttonFullWidth')}>
                  {t('OrgChart.OrgDetails.initiateTransferButton', { defaultValue: 'Initiate Transfer' })}
                </Button>
              )}
            </div>
          )}
        </Box>
      </div>
    </div>
  );
};
function getBreadcrumbs(tree: string[], onClick: (value: string) => void): JSX.Element {
  if (tree.length === 0) return <></>;

  const [expanded, setExpanded] = useState(false);
  const toggleExpanded = () => setExpanded(!expanded);

  const renderBreadcrumbItem = (item: string, index: number) => (
    <Fragment key={item}>
      <button type="button" onClick={() => onClick(item)}>
        {item}
      </button>
      {index < tree.length - 1 && ' > '}
    </Fragment>
  );

  const renderCollapsedBreadcrumbs = () => (
    <>
      {renderBreadcrumbItem(tree[0], 0)}
      {'>'}
      <button type="button" onClick={toggleExpanded}>
        ...
      </button>
      {'>'}
      {renderBreadcrumbItem(tree[tree.length - 1], tree.length - 1)}
    </>
  );

  const breadcrumbs = (
    <span className={styles.breadcrumbs}>
      {tree.length <= 2 || expanded ? tree.map(renderBreadcrumbItem) : renderCollapsedBreadcrumbs()}
    </span>
  );

  return <nav aria-label="Breadcrumbs">{breadcrumbs}</nav>;
}
export default OrgDetails;
